import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Suspense, lazy, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTracker from "@/components/PageTracker";
import WhatsAppWidget from "@/components/WhatsAppWidget";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 rounded-full border-4 border-[#004E89] border-t-transparent animate-spin" />
  </div>
);

// Core Landing Pages
import LandingPage from "@/pages/LandingPage";
const EnterSuperApp = lazy(() => import("@/pages/core/EnterSuperApp"));
const RequestDemo = lazy(() => import("@/pages/core/RequestDemo"));
const PlatformDemo = lazy(() => import("@/pages/core/PlatformDemo"));
const BecomePartner = lazy(() => import("@/pages/SignUpPage"));
const Announcements = lazy(() => import("@/pages/core/Announcements"));

// About Osaka Group
const AboutOsakaGroup = lazy(() => import("@/pages/about/AboutOsakaGroup"));
const OsakaLegacy = lazy(() => import("@/pages/about/OsakaLegacy"));
const GroupCompanies = lazy(() => import("@/pages/about/GroupCompanies"));
const ChairmanMessage = lazy(() => import("@/pages/about/ChairmanMessage"));
const LeadershipTeam = lazy(() => import("@/pages/about/LeadershipTeam"));
const AwardsRecognition = lazy(() => import("@/pages/about/AwardsRecognition"));

// Platform Overview
const WhatIsHypermarket = lazy(() => import("@/pages/platform/WhatIsHypermarket"));
const WhyOsakaConnect = lazy(() => import("@/pages/platform/WhyOsakaConnect"));
const PlatformArchitecture = lazy(() => import("@/pages/platform/PlatformArchitecture"));
const TechInfrastructure = lazy(() => import("@/pages/platform/TechInfrastructure"));
const AIToolsPage = lazy(() => import("@/pages/AIToolsPage"));
const SecurityCompliance = lazy(() => import("@/pages/platform/SecurityCompliance"));

// Products (24)
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const FlightsPage = lazy(() => import("@/pages/products/FlightsPage"));
const HotelsPage = lazy(() => import("@/pages/products/HotelsPage"));
const VisaPage = lazy(() => import("@/pages/products/VisaPage"));
const InsurancePage = lazy(() => import("@/pages/products/InsurancePage"));
const CruisePage = lazy(() => import("@/pages/products/CruisePage"));
const ActivitiesPage = lazy(() => import("@/pages/products/ActivitiesPage"));
const RailPage = lazy(() => import("@/pages/products/RailPage"));
const BusPage = lazy(() => import("@/pages/products/BusPage"));
const AirportServicesPage = lazy(() => import("@/pages/products/AirportServicesPage"));
const AirportLoungePage = lazy(() => import("@/pages/products/AirportLoungePage"));
const AirportFastTrackPage = lazy(() => import("@/pages/products/AirportFastTrackPage"));
const AirportAssistPage = lazy(() => import("@/pages/products/AirportAssistPage"));
const ForexPage = lazy(() => import("@/pages/products/ForexPage"));
const ForexCardsPage = lazy(() => import("@/pages/products/ForexCardsPage"));
const DynamicPackagesPage = lazy(() => import("@/pages/products/DynamicPackagesPage"));
const FixedDeparturesPage = lazy(() => import("@/pages/products/FixedDeparturesPage"));
const ESimPage = lazy(() => import("@/pages/products/ESimPage"));
const CarRentalsPage = lazy(() => import("@/pages/products/CarRentalsPage"));
const VillasPage = lazy(() => import("@/pages/products/VillasPage"));
const EntryTicketsPage = lazy(() => import("@/pages/products/EntryTicketsPage"));
const CarbonOffsetPage = lazy(() => import("@/pages/products/CarbonOffsetPage"));
const MedicalTourismPage = lazy(() => import("@/pages/products/MedicalTourismPage"));
const SBTPage = lazy(() => import("@/pages/products/SBTPage"));
const TravelAddonsPage = lazy(() => import("@/pages/products/TravelAddonsPage"));

// Partner Ecosystem
const B2BPartnersPage = lazy(() => import("@/pages/partners/B2BPartnersPage"));
const B2BBenefitsPage = lazy(() => import("@/pages/partners/B2BBenefitsPage"));
const B2BOnboardingPage = lazy(() => import("@/pages/partners/B2BOnboardingPage"));
const B2ECorporatePage = lazy(() => import("@/pages/partners/B2ECorporatePage"));
const B2EFeaturesPage = lazy(() => import("@/pages/partners/B2EFeaturesPage"));
const B2EImplementationPage = lazy(() => import("@/pages/partners/B2EImplementationPage"));
const StrategicPartnersPage = lazy(() => import("@/pages/partners/StrategicPartnersPage"));
const APIPartnersPage = lazy(() => import("@/pages/partners/APIPartnersPage"));
const ConsortiumPartnersPage = lazy(() => import("@/pages/partners/ConsortiumPartnersPage"));
const TourismMinistryPage = lazy(() => import("@/pages/partners/TourismMinistryPage"));
const WhiteLabelPage = lazy(() => import("@/pages/partners/WhiteLabelPage"));
const PartnershipsPage = lazy(() => import("@/pages/PartnershipsPage"));

// Technology & API
const DeveloperPortal = lazy(() => import("@/pages/developers/DeveloperPortal"));
const APIDocumentation = lazy(() => import("@/pages/developers/APIDocumentation"));
const APISandbox = lazy(() => import("@/pages/developers/APISandbox"));
const APIIntegration = lazy(() => import("@/pages/developers/APIIntegration"));
const APIPricing = lazy(() => import("@/pages/developers/APIPricing"));

// Resources
const CaseStudies = lazy(() => import("@/pages/resources/CaseStudies"));
const Testimonials = lazy(() => import("@/pages/resources/Testimonials"));
const BlogInsights = lazy(() => import("@/pages/resources/BlogInsights"));
const PressMedia = lazy(() => import("@/pages/resources/PressMedia"));
const EventsWebinars = lazy(() => import("@/pages/resources/EventsWebinars"));
const DownloadCenter = lazy(() => import("@/pages/resources/DownloadCenter"));

// Support
const HelpCenter = lazy(() => import("@/pages/support/HelpCenter"));
const KnowledgeBase = lazy(() => import("@/pages/support/KnowledgeBase"));
const CustomerSupport = lazy(() => import("@/pages/support/CustomerSupport"));
const PartnerSupport = lazy(() => import("@/pages/support/PartnerSupport"));
const TrainingAcademy = lazy(() => import("@/pages/support/TrainingAcademy"));

// Corporate & Legal
const Careers = lazy(() => import("@/pages/corporate/Careers"));
const InvestorRelations = lazy(() => import("@/pages/corporate/InvestorRelations"));
const PrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/legal/TermsOfService"));
const SecurityPolicy = lazy(() => import("@/pages/legal/SecurityPolicy"));
const GDPRCompliance = lazy(() => import("@/pages/legal/GDPRCompliance"));
const CookiePolicy = lazy(() => import("@/pages/legal/CookiePolicy"));

// Contact
const GlobalPresencePage = lazy(() => import("@/pages/GlobalPresencePage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const PartnershipInquiries = lazy(() => import("@/pages/contact/PartnershipInquiries"));
const CorporateSales = lazy(() => import("@/pages/contact/CorporateSales"));
const APISupport = lazy(() => import("@/pages/contact/APISupport"));
const MediaContact = lazy(() => import("@/pages/contact/MediaContact"));

// Sign Up
const SignUpPage = lazy(() => import("@/pages/SignUpPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

// Layout wrapper that hides Navbar/Footer on admin
const AppLayout = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  return (
    <>
      {!isAdmin && <Navbar />}
      <main>
        <Suspense fallback={<PageLoader />}>
          <Routes>
              {/* Core Landing */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/super-app" element={<EnterSuperApp />} />
              <Route path="/demo" element={<RequestDemo />} />
              <Route path="/platform-demo" element={<PlatformDemo />} />
              <Route path="/announcements" element={<Announcements />} />

              {/* About Osaka Group */}
              <Route path="/about" element={<AboutOsakaGroup />} />
              <Route path="/about/osaka-group" element={<AboutOsakaGroup />} />
              <Route path="/about/legacy" element={<OsakaLegacy />} />
              <Route path="/about/companies" element={<GroupCompanies />} />
              <Route path="/about/chairman" element={<ChairmanMessage />} />
              <Route path="/about/leadership" element={<LeadershipTeam />} />
              <Route path="/about/awards" element={<AwardsRecognition />} />

              {/* Platform Overview */}
              <Route path="/platform/hypermarket" element={<WhatIsHypermarket />} />
              <Route path="/platform/why-osaka" element={<WhyOsakaConnect />} />
              <Route path="/platform/architecture" element={<PlatformArchitecture />} />
              <Route path="/platform/technology" element={<TechInfrastructure />} />
              <Route path="/ai-tools" element={<AIToolsPage />} />
              <Route path="/platform/security" element={<SecurityCompliance />} />

              {/* Products */}
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/flights" element={<FlightsPage />} />
              <Route path="/products/hotels" element={<HotelsPage />} />
              <Route path="/products/visa" element={<VisaPage />} />
              <Route path="/products/insurance" element={<InsurancePage />} />
              <Route path="/products/cruise" element={<CruisePage />} />
              <Route path="/products/activities" element={<ActivitiesPage />} />
              <Route path="/products/rail" element={<RailPage />} />
              <Route path="/products/bus" element={<BusPage />} />
              <Route path="/products/airport-services" element={<AirportServicesPage />} />
              <Route path="/products/airport-lounge" element={<AirportLoungePage />} />
              <Route path="/products/airport-fast-track" element={<AirportFastTrackPage />} />
              <Route path="/products/airport-assist" element={<AirportAssistPage />} />
              <Route path="/products/forex" element={<ForexPage />} />
              <Route path="/products/forex-cards" element={<ForexCardsPage />} />
              <Route path="/products/dynamic-packages" element={<DynamicPackagesPage />} />
              <Route path="/products/fixed-departures" element={<FixedDeparturesPage />} />
              <Route path="/products/esim" element={<ESimPage />} />
              <Route path="/products/car-rentals" element={<CarRentalsPage />} />
              <Route path="/products/villas" element={<VillasPage />} />
              <Route path="/products/entry-tickets" element={<EntryTicketsPage />} />
              <Route path="/products/carbon-offset" element={<CarbonOffsetPage />} />
              <Route path="/products/medical-tourism" element={<MedicalTourismPage />} />
              <Route path="/products/sbt" element={<SBTPage />} />
              <Route path="/products/addons" element={<TravelAddonsPage />} />

              {/* Partner Ecosystem */}
              <Route path="/partnerships" element={<PartnershipsPage />} />
              <Route path="/partners/b2b" element={<B2BPartnersPage />} />
              <Route path="/partners/b2b/benefits" element={<B2BBenefitsPage />} />
              <Route path="/partners/b2b/onboarding" element={<B2BOnboardingPage />} />
              <Route path="/partners/b2e" element={<B2ECorporatePage />} />
              <Route path="/partners/b2e/features" element={<B2EFeaturesPage />} />
              <Route path="/partners/b2e/implementation" element={<B2EImplementationPage />} />
              <Route path="/partners/strategic" element={<StrategicPartnersPage />} />
              <Route path="/partners/api" element={<APIPartnersPage />} />
              <Route path="/partners/consortium" element={<ConsortiumPartnersPage />} />
              <Route path="/partners/tourism-ministry" element={<TourismMinistryPage />} />
              <Route path="/partners/whitelabel" element={<WhiteLabelPage />} />

              {/* Technology & API */}
              <Route path="/developers" element={<DeveloperPortal />} />
              <Route path="/developers/docs" element={<APIDocumentation />} />
              <Route path="/developers/sandbox" element={<APISandbox />} />
              <Route path="/developers/integration" element={<APIIntegration />} />
              <Route path="/developers/pricing" element={<APIPricing />} />

              {/* Resources */}
              <Route path="/resources/case-studies" element={<CaseStudies />} />
              <Route path="/resources/testimonials" element={<Testimonials />} />
              <Route path="/resources/blog" element={<BlogInsights />} />
              <Route path="/resources/press" element={<PressMedia />} />
              <Route path="/resources/events" element={<EventsWebinars />} />
              <Route path="/resources/downloads" element={<DownloadCenter />} />

              {/* Support */}
              <Route path="/support/help" element={<HelpCenter />} />
              <Route path="/support/knowledge" element={<KnowledgeBase />} />
              <Route path="/support/customer" element={<CustomerSupport />} />
              <Route path="/support/partner" element={<PartnerSupport />} />
              <Route path="/support/training" element={<TrainingAcademy />} />

              {/* Corporate & Legal */}
              <Route path="/corporate/careers" element={<Careers />} />
              <Route path="/corporate/investors" element={<InvestorRelations />} />
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms" element={<TermsOfService />} />
              <Route path="/legal/security" element={<SecurityPolicy />} />
              <Route path="/legal/gdpr" element={<GDPRCompliance />} />
              <Route path="/legal/cookies" element={<CookiePolicy />} />

              {/* Contact */}
              <Route path="/global-presence" element={<GlobalPresencePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/contact/partnership" element={<PartnershipInquiries />} />
              <Route path="/contact/sales" element={<CorporateSales />} />
              <Route path="/contact/api-support" element={<APISupport />} />
              <Route path="/contact/media" element={<MediaContact />} />

              {/* Sign Up */}
              <Route path="/signup" element={<SignUpPage />} />

              {/* Admin */}
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </Suspense>
        </main>
        {!isAdmin && <Footer />}
        {!isAdmin && <WhatsAppWidget />}
        <PageTracker />
        <Toaster position="top-right" richColors />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <ScrollToTop />
          <AppLayout />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
